// ============================================================================
// AM Route module — data layer. Single import surface for the AM Today's
// Route view, the Exec "Where are my AMs" board, and the rotation template
// editor.
//
// Backend (live in Supabase, do NOT migrate):
//   Tables  am_route_assignments · am_route_week_state ·
//           am_route_day_instances · am_route_day_stops
//   Views   v_am_routes_today · v_am_routes_week · v_am_route_template
//   RPC     am_route_current_parity(p_am_eid) → 'A' | 'B'
//
// Conventions mirrored from hr-hiring / supply-management:
//   * Flat fetch + IN-lookup + client-join (NO PostgREST resource embeds).
//   * TanStack-Query-shaped hook surface (data/isLoading/error/refetch) on
//     plain useState/useEffect — the project has no @tanstack/react-query.
//   * Mutations re-fetch from the DB (authoritative) rather than optimistic
//     local edits — this is a field tool; correctness beats micro-latency.
//
// IMPORTANT (verified 2026-05-28 against live Supabase): the am_route_*
// tables DO have BEFORE INSERT id-assign triggers (ARA/ARI/ARS-NNNN),
// updated_at triggers, audit triggers, and block-delete triggers — same
// house pattern as new_account_sop. Therefore:
//   * Do NOT supply PK columns on insert (assignment_id/instance_id/
//     day_stop_id) — the trigger assigns them. Use .insert(...).select()
//     to read the assigned id back when you need it (e.g. inserting child
//     day-stops after creating the day-instance).
//   * Do NOT stamp updated_at on UPDATE — the trigger maintains it.
//   * Deletes are blocked; use active=false for soft-removal.
// ============================================================================

import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '../../../shared/supabase';
import { errorMessage } from '../../../shared/errors';
import type { RagStatus } from '../../../shared/types';

// ---------------------------------------------------------------------------
// Value types — mirror the DB CHECK domains.
// ---------------------------------------------------------------------------

import { isPausedState, partitionByService, withServiceState } from '../lib/service-state';

export type RouteParity = 'A' | 'B';
// AFB-0063: how a row got onto a route. 'own' is DERIVED (facility
// assignment) and unremovable; 'assist' is a stored, freely-removable
// volunteer row; 'stop' is a non-facility stop (office etc.).
export type RouteCoverage = 'own' | 'assist' | 'stop';
export type WeekParity = 'A' | 'B' | 'A_B';
export type RouteCadence =
  | 'Weekly'
  | 'Biweekly'
  | 'Monthly'
  | 'Bimonthly'
  | 'As_Needed';
export type DayStopStatus = 'Planned' | 'Visited' | 'Skipped' | 'Cancelled';
export type DayOfWeek =
  | 'Monday'
  | 'Tuesday'
  | 'Wednesday'
  | 'Thursday'
  | 'Friday'
  | 'Saturday'
  | 'Sunday';

export const CADENCE_LABELS: Record<RouteCadence, string> = {
  Weekly: 'Weekly',
  Biweekly: 'Biweekly',
  Monthly: 'Monthly',
  Bimonthly: 'Bimonthly',
  As_Needed: 'As able',
};

export const WEEKDAYS: DayOfWeek[] = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
];

// ---------------------------------------------------------------------------
// Row types
// ---------------------------------------------------------------------------

export interface TodayViewRow {
  am_eid: string;
  am_name: string | null;
  region: string | null;
  current_parity: RouteParity | null;
  assignment_id: string;
  facility_id: string | null;
  stop_label: string | null;
  facility_name: string | null;
  account_status_rag: RagStatus | null;
  cadence: RouteCadence | null;
  week_parity: WeekParity | null;
  day_of_week: DayOfWeek | null;
  sort_order: number | null;
  note: string | null;
  coverage: RouteCoverage | null;
  owner_name: string | null;
  // Enriched client-side. None of the route views expose `service_state` and
  // `active_facilities` must not be changed, so it arrives by IN-lookup and
  // client-join (see lib/service-state.ts). Undefined only on a row that was
  // never enriched; 'Active' is represented by NULL in the database, so treat
  // null AND undefined as Active.
  service_state?: string | null;
}

export interface WeekViewRow extends TodayViewRow {
  is_as_able: boolean | null;
  week_of_month: number | null;
  poc_initials: string | null;
  // Enriched client-side (useWeekRoute withContacts): the owner / main
  // contact for the facility — service_assignments.worker_name, else the
  // provider's primary_poc_name. null when neither is known (callers fall
  // back to poc_initials). Lets AMs recognize a stop by who runs it.
  display_contact?: string | null;
}

export interface DayInstanceRow {
  instance_id: string;
  am_eid: string;
  route_date: string;
  planned_parity: RouteParity | null;
  status: string;
  notes: string | null;
}

export interface DayStopRow {
  day_stop_id: string;
  instance_id: string;
  assignment_id: string | null;
  facility_id: string | null;
  stop_label: string | null;
  stop_order: number | null;
  status: DayStopStatus;
  cancel_reason: string | null;
  is_ad_hoc: boolean;
  visited_at: string | null;
  notes: string | null;
}

export interface TemplateRow {
  // NULL for owned-but-unscheduled facilities (no schedule row yet) — the
  // signal that writes must go through am_route_set_schedule (INSERT path).
  assignment_id: string | null;
  am_eid: string;
  am_name: string | null;
  region: string | null;
  facility_id: string | null;
  facility_name: string | null;
  facility_status: string | null;
  account_status_rag: RagStatus | null;
  has_geo: boolean | null;
  stop_label: string | null;
  stop_type: string | null;
  cadence: RouteCadence | null;
  week_parity: WeekParity | null;
  week_of_month: number | null;
  day_of_week: DayOfWeek | null;
  is_as_able: boolean | null;
  sort_order: number | null;
  poc_initials: string | null;
  note: string | null;
  active: boolean;
  // Week-of-month ordinals (1–5) on which a Weekly stop is suppressed.
  exclude_weeks: number[] | null;
  // AFB-0063 derived-union columns.
  coverage: RouteCoverage;
  is_removable: boolean;
  owner_eid: string | null;
  owner_name: string | null;
  // Enriched client-side, as on TodayViewRow.
  service_state?: string | null;
}

// A stop the Today view renders, whether it came straight from the template
// view (not yet materialized) or from the day-stops override layer (after an
// edit). `key` is stable for React lists; `dayStopId` is null until the day
// is materialized.
export interface ResolvedStop {
  key: string;
  dayStopId: string | null;
  assignmentId: string | null;
  facilityId: string | null;
  label: string;
  rag: RagStatus | null;
  cadence: RouteCadence | null;
  weekParity: WeekParity | null;
  note: string | null;
  status: DayStopStatus;
  cancelReason: string | null;
  isAdHoc: boolean;
  order: number;
  // 'Active' | 'Suspended' | 'Ended'. NULL/absent means Active — the state of
  // almost every facility. A Suspended stop STAYS on the route and is badged;
  // it is never dropped (Ben's ruling on the route leg).
  serviceState: string | null;
}

// ---------------------------------------------------------------------------
// Date + id helpers
// ---------------------------------------------------------------------------

// Local YYYY-MM-DD (not UTC) — the AM's calendar day, matches DB `date` cols.
export function localDateISO(d: Date = new Date()): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

// Monday of the current week, local. Written to am_route_week_state.
// effective_week_start when an AM flips A↔B (handoff §3A).
export function isoMondayLocal(d: Date = new Date()): string {
  const base = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const day = base.getDay(); // 0 Sun .. 6 Sat
  const diff = day === 0 ? -6 : 1 - day;
  base.setDate(base.getDate() + diff);
  return localDateISO(base);
}

const nowISO = () => new Date().toISOString();

/**
 * facility_id -> service_state, for the facilities on a route.
 *
 * One IN-lookup on `facilities`. See lib/service-state.ts for why this is a
 * client-side join rather than a column on the route views.
 *
 * **Returns {} on failure instead of throwing.** A missing badge is a
 * degraded route; a thrown error is no route at all, and an AM standing in a
 * car park needs their stops more than they need the badge. The failure is
 * logged so it is diagnosable rather than silent.
 */
async function fetchServiceStates(
  facilityIds: Array<string | null | undefined>,
): Promise<Record<string, string | null>> {
  const ids = Array.from(new Set(facilityIds.filter(Boolean))) as string[];
  if (ids.length === 0) return {};
  const { data, error } = await supabase
    .from('facilities')
    .select('facility_id, service_state')
    .in('facility_id', ids);
  if (error) {
    console.error('[am-routes] service_state lookup failed — route renders unbadged', error);
    return {};
  }
  const map: Record<string, string | null> = {};
  for (const r of (data ?? []) as Array<{
    facility_id: string;
    service_state: string | null;
  }>) {
    map[r.facility_id] = r.service_state;
  }
  return map;
}

// NOTE: PK ids (ARA/ARI/ARS-NNNN) are assigned by BEFORE INSERT triggers on
// the am_route_* tables — never supply them client-side. Use
// .insert(...).select().single() to read the trigger-assigned id back when a
// child row needs the parent's id. updated_at is maintained by trigger too.

const TODAY_COLS =
  'am_eid, am_name, region, current_parity, assignment_id, facility_id, ' +
  'stop_label, facility_name, account_status_rag, cadence, week_parity, ' +
  'day_of_week, sort_order, note, coverage, owner_name';

const WEEK_COLS =
  'am_eid, am_name, region, current_parity, assignment_id, facility_id, ' +
  'stop_label, facility_name, account_status_rag, cadence, week_parity, ' +
  'week_of_month, day_of_week, is_as_able, sort_order, poc_initials, note, ' +
  'coverage, owner_name';

const DAY_STOP_COLS =
  'day_stop_id, instance_id, assignment_id, facility_id, stop_label, ' +
  'stop_order, status, cancel_reason, is_ad_hoc, visited_at, notes';

const TEMPLATE_COLS =
  'assignment_id, am_eid, am_name, region, facility_id, facility_name, ' +
  'facility_status, account_status_rag, has_geo, stop_label, stop_type, ' +
  'cadence, week_parity, week_of_month, day_of_week, is_as_able, ' +
  'sort_order, poc_initials, note, active, exclude_weeks, ' +
  'coverage, is_removable, owner_eid, owner_name';

// ---------------------------------------------------------------------------
// useTodayRoute — the spine of the AM Today's Route view.
//
// Resolution order:
//   1. v_am_routes_today (parity + monthly aware) is the baseline.
//   2. If an am_route_day_instance exists for (am_eid, today), the day-stops
//      layer OVERRIDES the baseline — render those rows (incl. skips + ad-hoc).
//   3. Facility name + RAG for day-stops are enriched from `facilities`
//      (day_stops only persist facility_id + stop_label).
// ---------------------------------------------------------------------------

export interface TodayRouteResult {
  parity: RouteParity | null;
  isMaterialized: boolean;
  instanceId: string | null;
  stops: ResolvedStop[];
  /**
   * Paused facilities taken OFF today's list, counted by facility. The header
   * shows this so a filtered list never reads as a shorter one — Ben wants the
   * stop hidden, and his standing rule is that nothing disappears silently.
   */
  pausedHidden: number;
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
  flipParity: (next: RouteParity) => Promise<void>;
  skipStop: (
    target: { dayStopId?: string | null; assignmentId?: string | null },
    reason: string,
  ) => Promise<void>;
  visitStop: (target: {
    dayStopId?: string | null;
    assignmentId?: string | null;
  }) => Promise<void>;
  resetStop: (target: {
    dayStopId?: string | null;
    assignmentId?: string | null;
  }) => Promise<void>;
  addAdHoc: (input: {
    facility_id: string | null;
    stop_label: string;
  }) => Promise<void>;
  reorder: (orderedKeys: string[]) => Promise<void>;
}

export function useTodayRoute(
  amEid: string | null,
  actorEid?: string | null,
): TodayRouteResult {
  const [baseline, setBaseline] = useState<TodayViewRow[]>([]);
  const [instance, setInstance] = useState<DayInstanceRow | null>(null);
  const [dayStops, setDayStops] = useState<DayStopRow[]>([]);
  const [facMap, setFacMap] = useState<
    Record<
      string,
      { name: string | null; rag: RagStatus | null; serviceState: string | null }
    >
  >({});
  const [parity, setParity] = useState<RouteParity | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  const writer = actorEid ?? amEid ?? null;
  const dateStr = localDateISO();
  const refetch = useCallback(() => setTick((n) => n + 1), []);

  useEffect(() => {
    if (!amEid) {
      setBaseline([]);
      setInstance(null);
      setDayStops([]);
      setParity(null);
      setIsLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setIsLoading(true);
      setError(null);
      try {
        const [todayRes, instRes, parityRes] = await Promise.all([
          supabase
            .from('v_am_routes_today')
            .select(TODAY_COLS)
            .eq('am_eid', amEid)
            .order('sort_order', { ascending: true }),
          supabase
            .from('am_route_day_instances')
            .select('instance_id, am_eid, route_date, planned_parity, status, notes')
            .eq('am_eid', amEid)
            .eq('route_date', dateStr)
            .eq('active', true)
            .maybeSingle(),
          supabase.rpc('am_route_current_parity', { p_am_eid: amEid }),
        ]);
        if (cancelled) return;
        if (todayRes.error) throw todayRes.error;
        const todayRows = (todayRes.data ?? []) as unknown as TodayViewRow[];
        const inst = (instRes.data ?? null) as DayInstanceRow | null;

        // Parity: RPC is authoritative; fall back to the view's column.
        const rpcParity = (parityRes.data as RouteParity | null) ?? null;
        const viewParity = todayRows[0]?.current_parity ?? null;
        setParity(rpcParity ?? viewParity);

        let stops: DayStopRow[] = [];
        if (inst) {
          const { data: stopData, error: stopErr } = await supabase
            .from('am_route_day_stops')
            .select(DAY_STOP_COLS)
            .eq('instance_id', inst.instance_id)
            .eq('active', true)
            .order('stop_order', { ascending: true });
          if (cancelled) return;
          if (stopErr) throw stopErr;
          stops = (stopData ?? []) as unknown as DayStopRow[];
        }

        // Enrich facility name + RAG for the rendered set. Baseline rows carry
        // it already; day-stops don't, so IN-lookup facilities for any ids.
        const facIds = Array.from(
          new Set(
            [
              ...todayRows.map((r) => r.facility_id),
              ...stops.map((s) => s.facility_id),
            ].filter(Boolean),
          ),
        ) as string[];
        const map: Record<
          string,
          { name: string | null; rag: RagStatus | null; serviceState: string | null }
        > = {};
        for (const r of todayRows) {
          if (r.facility_id)
            map[r.facility_id] = {
              name: r.facility_name,
              rag: r.account_status_rag,
              serviceState: null,
            };
        }
        const missing = facIds.filter((id) => !(id in map));
        if (missing.length > 0) {
          const { data: facData } = await supabase
            .from('facilities')
            .select('facility_id, facility_name, account_status_rag')
            .in('facility_id', missing);
          for (const f of (facData ?? []) as Array<{
            facility_id: string;
            facility_name: string | null;
            account_status_rag: RagStatus | null;
          }>) {
            map[f.facility_id] = {
              name: f.facility_name,
              rag: f.account_status_rag,
              serviceState: null,
            };
          }
        }

        // service_state for EVERY facility on the route, not just the ones
        // missing above: v_am_routes_today does not carry it, so the rows that
        // came from the view need it too.
        const stateByFac = await fetchServiceStates(facIds);
        for (const id of Object.keys(map)) {
          map[id].serviceState = stateByFac[id] ?? null;
        }

        if (cancelled) return;
        setBaseline(todayRows);
        setInstance(inst);
        setDayStops(stops);
        setFacMap(map);
      } catch (e) {
        if (cancelled) return;
        console.error('[am-routes] today load failed', e);
        setError(errorMessage(e, 'Failed to load route.'));
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [amEid, dateStr, tick]);

  // ----- Derived: the rendered stop list ---------------------------------
  // A paused facility is dropped from TODAY (Ben, 2026-08-21). It stays on the
  // template, badged — that is the rotation, and the rotation survives a pause.
  // Nothing is deleted here or in the database: `rpc_pause_facility` skips the
  // already-materialised day stops and `rpc_resume_facility` puts back exactly
  // those, so this filter is the only thing standing between a pause and the
  // stop reappearing.
  const allStops = useMemo<ResolvedStop[]>(() => {
    if (instance) {
      return [...dayStops]
        .sort((a, b) => (a.stop_order ?? 0) - (b.stop_order ?? 0))
        .map((s) => {
          const base = baseline.find((r) => r.assignment_id === s.assignment_id);
          const fac = s.facility_id ? facMap[s.facility_id] : undefined;
          return {
            key: s.day_stop_id,
            dayStopId: s.day_stop_id,
            assignmentId: s.assignment_id,
            facilityId: s.facility_id,
            label: fac?.name ?? s.stop_label ?? 'Untitled stop',
            rag: fac?.rag ?? null,
            serviceState: fac?.serviceState ?? null,
            cadence: base?.cadence ?? null,
            weekParity: base?.week_parity ?? null,
            note: base?.note ?? s.notes ?? null,
            status: s.status,
            cancelReason: s.cancel_reason,
            isAdHoc: s.is_ad_hoc,
            order: s.stop_order ?? 0,
          };
        });
    }
    return baseline.map((r, i) => ({
      key: r.assignment_id,
      dayStopId: null,
      assignmentId: r.assignment_id,
      facilityId: r.facility_id,
      label: r.facility_name ?? r.stop_label ?? 'Untitled stop',
      rag: r.account_status_rag,
      serviceState: r.facility_id ? facMap[r.facility_id]?.serviceState ?? null : null,
      cadence: r.cadence,
      weekParity: r.week_parity,
      note: r.note,
      status: 'Planned' as DayStopStatus,
      cancelReason: null,
      isAdHoc: false,
      order: r.sort_order ?? i,
    }));
  }, [instance, dayStops, baseline, facMap]);

  const stops = useMemo(
    () => allStops.filter((s) => !isPausedState(s.serviceState)),
    [allStops],
  );
  const pausedHidden = useMemo(() => {
    const seen = new Set<string>();
    for (const s of allStops) {
      if (s.facilityId && isPausedState(s.serviceState)) seen.add(s.facilityId);
    }
    return seen.size;
  }, [allStops]);

  // ----- Mutations -------------------------------------------------------

  const flipParity = useCallback(
    async (next: RouteParity) => {
      if (!amEid) return;
      const { error: e } = await supabase
        .from('am_route_week_state')
        .upsert(
          {
            am_eid: amEid,
            current_parity: next,
            effective_week_start: isoMondayLocal(),
            updated_by: writer,
          },
          { onConflict: 'am_eid' },
        );
      if (e) throw e;
      refetch();
    },
    [amEid, writer, refetch],
  );

  // Materialize the day on first edit: create the instance + mirror today's
  // baseline into day_stops. Idempotent — re-checks for an existing instance
  // first. Returns the live stop rows so callers can map assignment → stop.
  const ensureInstance = useCallback(async (): Promise<{
    instanceId: string;
    stops: DayStopRow[];
  }> => {
    if (!amEid) throw new Error('No AM context.');
    const { data: existing, error: exErr } = await supabase
      .from('am_route_day_instances')
      .select('instance_id')
      .eq('am_eid', amEid)
      .eq('route_date', dateStr)
      .eq('active', true)
      .maybeSingle();
    if (exErr) throw exErr;
    if (existing) {
      const { data: existingStops, error: sErr } = await supabase
        .from('am_route_day_stops')
        .select(DAY_STOP_COLS)
        .eq('instance_id', existing.instance_id)
        .eq('active', true)
        .order('stop_order', { ascending: true });
      if (sErr) throw sErr;
      return {
        instanceId: existing.instance_id as string,
        stops: (existingStops ?? []) as unknown as DayStopRow[],
      };
    }

    // Create the day-instance; the trigger assigns instance_id, read it back.
    const { data: instRow, error: insErr } = await supabase
      .from('am_route_day_instances')
      .insert({
        am_eid: amEid,
        route_date: dateStr,
        planned_parity: parity,
        status: 'Planned',
        created_by: writer,
      })
      .select('instance_id')
      .single();
    if (insErr) throw insErr;
    const instId = (instRow as { instance_id: string }).instance_id;

    const payload = baseline.map((r, i) => ({
      instance_id: instId,
      assignment_id: r.assignment_id,
      facility_id: r.facility_id,
      stop_label: r.facility_name ?? r.stop_label ?? 'Untitled stop',
      stop_order: r.sort_order ?? i,
      status: 'Planned',
      is_ad_hoc: false,
      created_by: writer,
    }));
    let createdStops: DayStopRow[] = [];
    if (payload.length > 0) {
      // Insert stops; trigger assigns day_stop_id. Read back so callers
      // (resolveStopId) can match on the real ids.
      const { data: stopRows, error: stopErr } = await supabase
        .from('am_route_day_stops')
        .insert(payload)
        .select(DAY_STOP_COLS);
      if (stopErr) throw stopErr;
      createdStops = (stopRows ?? []) as unknown as DayStopRow[];
    }
    return { instanceId: instId, stops: createdStops };
  }, [amEid, dateStr, parity, baseline, writer]);

  // Resolve a {dayStopId|assignmentId} target to a concrete day_stop_id,
  // materializing the day if needed.
  const resolveStopId = useCallback(
    async (target: {
      dayStopId?: string | null;
      assignmentId?: string | null;
    }): Promise<string | null> => {
      if (target.dayStopId) return target.dayStopId;
      const { stops: live } = await ensureInstance();
      if (target.assignmentId) {
        return (
          live.find((s) => s.assignment_id === target.assignmentId)
            ?.day_stop_id ?? null
        );
      }
      return null;
    },
    [ensureInstance],
  );

  const patchStop = useCallback(
    async (dayStopId: string, patch: Record<string, unknown>) => {
      const { error: e } = await supabase
        .from('am_route_day_stops')
        .update({ ...patch })
        .eq('day_stop_id', dayStopId);
      if (e) throw e;
    },
    [],
  );

  const skipStop = useCallback(
    async (
      target: { dayStopId?: string | null; assignmentId?: string | null },
      reason: string,
    ) => {
      const id = await resolveStopId(target);
      if (!id) throw new Error('Could not resolve stop to skip.');
      await patchStop(id, {
        status: 'Skipped',
        cancel_reason: reason.trim() || null,
      });
      refetch();
    },
    [resolveStopId, patchStop, refetch],
  );

  const visitStop = useCallback(
    async (target: { dayStopId?: string | null; assignmentId?: string | null }) => {
      const id = await resolveStopId(target);
      if (!id) throw new Error('Could not resolve stop.');
      await patchStop(id, { status: 'Visited', visited_at: nowISO() });
      refetch();
    },
    [resolveStopId, patchStop, refetch],
  );

  const resetStop = useCallback(
    async (target: { dayStopId?: string | null; assignmentId?: string | null }) => {
      const id = await resolveStopId(target);
      if (!id) throw new Error('Could not resolve stop.');
      await patchStop(id, {
        status: 'Planned',
        visited_at: null,
        cancel_reason: null,
      });
      refetch();
    },
    [resolveStopId, patchStop, refetch],
  );

  const addAdHoc = useCallback(
    async (input: { facility_id: string | null; stop_label: string }) => {
      const { instanceId, stops: live } = await ensureInstance();
      const maxOrder = live.reduce(
        (m, s) => Math.max(m, s.stop_order ?? 0),
        -1,
      );
      const { error: e } = await supabase.from('am_route_day_stops').insert({
        instance_id: instanceId,
        assignment_id: null,
        facility_id: input.facility_id,
        stop_label: input.stop_label.trim() || 'Ad-hoc stop',
        stop_order: maxOrder + 1,
        status: 'Planned',
        is_ad_hoc: true,
        created_by: writer,
      });
      if (e) throw e;
      refetch();
    },
    [ensureInstance, writer, refetch],
  );

  // Reorder: orderedKeys are ResolvedStop.key values (day_stop_id when
  // materialized, else assignment_id). Materialize, map keys → day_stop_ids,
  // and write stop_order = index for each.
  // NOTE: reorder is driven by the caller passing keys from `stops` (the
  // filtered list). A hidden paused stop is simply absent from that array, so
  // its stop_order is left exactly as it was — it is not renumbered, and it
  // returns to its old position on resume.
  const reorder = useCallback(
    async (orderedKeys: string[]) => {
      const { stops: live } = await ensureInstance();
      const byAssignment: Record<string, string> = {};
      const byId: Record<string, string> = {};
      for (const s of live) {
        byId[s.day_stop_id] = s.day_stop_id;
        if (s.assignment_id) byAssignment[s.assignment_id] = s.day_stop_id;
      }
      await Promise.all(
        orderedKeys.map((key, i) => {
          const dayStopId = byId[key] ?? byAssignment[key];
          if (!dayStopId) return Promise.resolve();
          return supabase
            .from('am_route_day_stops')
            .update({ stop_order: i })
            .eq('day_stop_id', dayStopId);
        }),
      );
      refetch();
    },
    [ensureInstance, refetch],
  );

  return useMemo(
    () => ({
      pausedHidden,
      parity,
      isMaterialized: instance != null,
      instanceId: instance?.instance_id ?? null,
      stops,
      isLoading,
      error,
      refetch,
      flipParity,
      skipStop,
      visitStop,
      resetStop,
      addAdHoc,
      reorder,
    }),
    [
      pausedHidden,
      parity,
      instance,
      stops,
      isLoading,
      error,
      refetch,
      flipParity,
      skipStop,
      visitStop,
      resetStop,
      addAdHoc,
      reorder,
    ],
  );
}

// ---------------------------------------------------------------------------
// useWeekRoute — v_am_routes_week. Pass an amEid to scope (AM as-able list);
// pass null to fetch every AM (Exec "this week" board).
// ---------------------------------------------------------------------------

export interface WeekRouteResult {
  rows: WeekViewRow[];
  /** Paused facilities dropped from the week, counted by facility. */
  pausedHidden: number;
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
}

export function useWeekRoute(
  amEid: string | null | undefined,
  opts?: { withContacts?: boolean },
): WeekRouteResult {
  const withContacts = opts?.withContacts ?? false;
  const [rows, setRows] = useState<WeekViewRow[]>([]);
  const [pausedHidden, setPausedHidden] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  const refetch = useCallback(() => setTick((n) => n + 1), []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setIsLoading(true);
      setError(null);
      try {
        let q = supabase.from('v_am_routes_week').select(WEEK_COLS);
        if (amEid) q = q.eq('am_eid', amEid);
        const { data, error: e } = await q
          .order('day_of_week', { ascending: true })
          .order('sort_order', { ascending: true });
        if (cancelled) return;
        if (e) throw e;
        let weekRows = (data ?? []) as unknown as WeekViewRow[];

        // Owner/POC enrichment: facility_id → worker_name (active assignment)
        // else provider primary_poc_name. Flat-fetch + IN-lookup + client-join
        // (no PostgREST embeds), per house pattern. Opt-in so the exec board
        // (all AMs) doesn't pay for it.
        if (withContacts && weekRows.length > 0) {
          const facIds = Array.from(
            new Set(weekRows.map((r) => r.facility_id).filter(Boolean)),
          ) as string[];
          if (facIds.length > 0) {
            const { data: saData } = await supabase
              .from('service_assignments')
              .select('facility_id, provider_id, worker_name, assignment_status')
              .in('facility_id', facIds)
              .eq('assignment_status', 'Active');
            const sas = (saData ?? []) as Array<{
              facility_id: string;
              provider_id: string | null;
              worker_name: string | null;
              assignment_status: string | null;
            }>;
            const provIds = Array.from(
              new Set(sas.map((s) => s.provider_id).filter(Boolean)),
            ) as string[];
            const pocByProvider: Record<string, string | null> = {};
            if (provIds.length > 0) {
              const { data: spData } = await supabase
                .from('service_providers')
                .select('provider_id, primary_poc_name')
                .in('provider_id', provIds);
              for (const p of (spData ?? []) as Array<{
                provider_id: string;
                primary_poc_name: string | null;
              }>) {
                pocByProvider[p.provider_id] = p.primary_poc_name;
              }
            }
            // One contact per facility: first active assignment with a name.
            const contactByFac: Record<string, string | null> = {};
            for (const s of sas) {
              if (contactByFac[s.facility_id]) continue;
              const poc = s.provider_id ? pocByProvider[s.provider_id] : null;
              const name = s.worker_name?.trim() || poc?.trim() || null;
              if (name) contactByFac[s.facility_id] = name;
            }
            weekRows = weekRows.map((r) => ({
              ...r,
              display_contact: r.facility_id
                ? contactByFac[r.facility_id] ?? null
                : null,
            }));
          }
        }

        // Service state, ALWAYS — deliberately not behind `withContacts`.
        // The exec board opts out of contact enrichment, and a paused facility
        // that shows a badge on the AM's own week but not on the board the
        // exec reads is worse than no badge at all.
        if (weekRows.length > 0) {
          const stateByFac = await fetchServiceStates(
            weekRows.map((r) => r.facility_id),
          );
          weekRows = withServiceState(weekRows, stateByFac);
        }

        if (cancelled) return;
        // A paused facility comes off THIS WEEK too (Ben, 2026-08-21). Counted
        // by facility, not by row: a weekly stop appears once per scheduled
        // day, so counting rows would say 3 where the AM lost one site.
        const split = partitionByService(weekRows);
        setRows(split.serviced);
        setPausedHidden(new Set(split.paused.map((r) => r.facility_id)).size);
      } catch (e) {
        if (cancelled) return;
        console.error('[am-routes] week load failed', e);
        setError(errorMessage(e, 'Failed to load week.'));
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [amEid, tick, withContacts]);

  return useMemo(
    () => ({ rows, pausedHidden, isLoading, error, refetch }),
    [rows, pausedHidden, isLoading, error, refetch],
  );
}

// ---------------------------------------------------------------------------
// useWeekState — the AM's am_route_week_state (current_parity +
// effective_week_start) that drives the This Week / Next Week parity math.
// Falls back to the am_route_current_parity RPC when no state row exists yet
// (most AMs haven't flipped a week), so the helper still resolves a parity.
// ---------------------------------------------------------------------------

export interface AmWeekState {
  current_parity: RouteParity | null;
  effective_week_start: string | null;
}

export function useWeekState(amEid: string | null): {
  state: AmWeekState;
  isLoading: boolean;
} {
  const [state, setState] = useState<AmWeekState>({
    current_parity: null,
    effective_week_start: null,
  });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!amEid) {
      setState({ current_parity: null, effective_week_start: null });
      setIsLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setIsLoading(true);
      try {
        const [stateRes, parityRes] = await Promise.all([
          supabase
            .from('am_route_week_state')
            .select('current_parity, effective_week_start')
            .eq('am_eid', amEid)
            .maybeSingle(),
          supabase.rpc('am_route_current_parity', { p_am_eid: amEid }),
        ]);
        if (cancelled) return;
        const row = (stateRes.data ?? null) as AmWeekState | null;
        const rpcParity = (parityRes.data as RouteParity | null) ?? null;
        setState({
          current_parity: row?.current_parity ?? rpcParity,
          effective_week_start: row?.effective_week_start ?? null,
        });
      } catch (e) {
        if (cancelled) return;
        console.error('[am-routes] week state load failed', e);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [amEid]);

  return useMemo(() => ({ state, isLoading }), [state, isLoading]);
}

// ---------------------------------------------------------------------------
// useTemplate — v_am_route_template for one AM, plus assignment writes.
// ---------------------------------------------------------------------------

export interface TemplatePatch {
  cadence?: RouteCadence;
  week_parity?: WeekParity | null;
  day_of_week?: DayOfWeek | null;
  is_as_able?: boolean;
  week_of_month?: number | null;
  sort_order?: number;
  stop_label?: string;
  note?: string | null;
  active?: boolean;
  exclude_weeks?: number[];
}

export interface NewAssignmentInput {
  am_eid: string;
  region: string | null;
  facility_id: string | null;
  stop_label: string;
  stop_type?: string;
  cadence: RouteCadence;
  week_parity: WeekParity | null;
  week_of_month: number | null;
  day_of_week: DayOfWeek | null;
  is_as_able: boolean;
  note: string | null;
}

// Schedule an ownership row (AFB-0063). Routes through the
// am_route_set_schedule RPC — INSERTs a 'schedule' row when the facility is
// owned-but-unscheduled (assignment_id NULL in the view), UPDATEs otherwise.
// day_of_week null = return to Unscheduled (never deletes the facility).
export interface SetScheduleInput {
  facility_id: string;
  day_of_week: DayOfWeek | null;
  cadence?: RouteCadence | null;
  week_parity?: WeekParity | null;
  sort_order?: number | null;
}

// Volunteer coverage at a facility the AM does not own (AFB-0063 'assist').
export interface NewAssistInput {
  facility_id: string;
  stop_label: string;
  day_of_week: DayOfWeek | null;
  cadence: RouteCadence;
  week_parity: WeekParity | null;
  note?: string | null;
}

export interface TemplateResult {
  rows: TemplateRow[];
  // Active assists OTHER employees hold on facilities this AM owns (D2) —
  // fetched from the same view in the same load (one query, not per-row).
  assistsIn: TemplateRow[];
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
  updateAssignment: (assignmentId: string, patch: TemplatePatch) => Promise<void>;
  createAssignment: (input: NewAssignmentInput, actorEid?: string | null) => Promise<string>;
  setActive: (assignmentId: string, active: boolean) => Promise<void>;
  setSchedule: (input: SetScheduleInput) => Promise<void>;
  createAssist: (input: NewAssistInput, actorEid?: string | null) => Promise<string>;
}

export function useTemplate(amEid: string | null): TemplateResult {
  const [rows, setRows] = useState<TemplateRow[]>([]);
  const [assistsIn, setAssistsIn] = useState<TemplateRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  const refetch = useCallback(() => setTick((n) => n + 1), []);

  useEffect(() => {
    if (!amEid) {
      setRows([]);
      setAssistsIn([]);
      setIsLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setIsLoading(true);
      setError(null);
      try {
        const [mineRes, assistsRes] = await Promise.all([
          supabase
            .from('v_am_route_template')
            .select(TEMPLATE_COLS)
            .eq('am_eid', amEid)
            .order('day_of_week', { ascending: true })
            .order('sort_order', { ascending: true }),
          supabase
            .from('v_am_route_template')
            .select(TEMPLATE_COLS)
            .eq('owner_eid', amEid)
            .eq('coverage', 'assist'),
        ]);
        if (cancelled) return;
        if (mineRes.error) throw mineRes.error;
        if (assistsRes.error) throw assistsRes.error;
        const mine = (mineRes.data ?? []) as unknown as TemplateRow[];
        const assists = (assistsRes.data ?? []) as unknown as TemplateRow[];
        // One lookup for both lists — the same facility often appears in each.
        const stateByFac = await fetchServiceStates([
          ...mine.map((r) => r.facility_id),
          ...assists.map((r) => r.facility_id),
        ]);
        if (cancelled) return;
        setRows(withServiceState(mine, stateByFac));
        setAssistsIn(withServiceState(assists, stateByFac));
      } catch (e) {
        if (cancelled) return;
        console.error('[am-routes] template load failed', e);
        setError(errorMessage(e, 'Failed to load template.'));
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [amEid, tick]);

  const updateAssignment = useCallback(
    async (assignmentId: string, patch: TemplatePatch) => {
      const payload: Record<string, unknown> = {};
      for (const [k, v] of Object.entries(patch)) payload[k] = v;
      const { error: e } = await supabase
        .from('am_route_assignments')
        .update(payload)
        .eq('assignment_id', assignmentId);
      if (e) throw e;
      refetch();
    },
    [refetch],
  );

  const createAssignment = useCallback(
    async (input: NewAssignmentInput, actorEid?: string | null) => {
      const { data: row, error: e } = await supabase.from('am_route_assignments').insert({
        am_eid: input.am_eid,
        region: input.region,
        facility_id: input.facility_id,
        stop_label: input.stop_label.trim(),
        stop_type: input.stop_type ?? 'Facility',
        cadence: input.cadence,
        week_parity: input.week_parity,
        week_of_month: input.week_of_month,
        day_of_week: input.day_of_week,
        is_as_able: input.is_as_able,
        note: input.note,
        active: true,
        created_by: actorEid ?? input.am_eid,
      })
        .select('assignment_id')
        .single();
      if (e) throw e;
      refetch();
      return (row as { assignment_id: string }).assignment_id;
    },
    [refetch],
  );

  const setActive = useCallback(
    async (assignmentId: string, active: boolean) => {
      const { error: e } = await supabase
        .from('am_route_assignments')
        .update({ active })
        .eq('assignment_id', assignmentId);
      if (e) throw e;
      refetch();
    },
    [refetch],
  );

  // AFB-0063: ownership scheduling goes through the SECURITY DEFINER RPC —
  // it INSERTs when no schedule row exists, UPDATEs when one does, and a
  // NULL day returns the facility to Unscheduled without deleting anything.
  // NOTE: PostgrestError is NOT an Error instance — normalize via
  // errorMessage() so auth failures ('You can only schedule your own route')
  // actually reach the toast instead of a generic fallback.
  const setSchedule = useCallback(
    async (input: SetScheduleInput) => {
      if (!amEid) throw new Error('No AM context.');
      const { error: e } = await supabase.rpc('am_route_set_schedule', {
        p_am_eid: amEid,
        p_facility_id: input.facility_id,
        p_day_of_week: input.day_of_week,
        p_cadence: input.cadence ?? null,
        p_week_parity: input.week_parity ?? null,
        p_sort_order: input.sort_order ?? null,
      });
      if (e) throw new Error(errorMessage(e, 'Could not update the schedule.'));
      refetch();
    },
    [amEid, refetch],
  );

  // AFB-0063: an assist row IS the coverage — created by any full employee
  // at a facility they do not own; freely removable by its creator.
  const createAssist = useCallback(
    async (input: NewAssistInput, actorEid?: string | null) => {
      if (!amEid) throw new Error('No AM context.');
      const { data: row, error: e } = await supabase
        .from('am_route_assignments')
        .insert({
          am_eid: amEid,
          facility_id: input.facility_id,
          stop_label: input.stop_label.trim() || 'Facility',
          stop_type: 'Facility',
          cadence: input.cadence,
          week_parity: input.cadence === 'Biweekly' ? input.week_parity : null,
          day_of_week: input.day_of_week,
          is_as_able: input.day_of_week === null,
          note: input.note ?? null,
          coverage_type: 'assist',
          active: true,
          created_by: actorEid ?? amEid,
        })
        .select('assignment_id')
        .single();
      if (e) throw new Error(errorMessage(e, 'Could not add the assist.'));
      refetch();
      return (row as { assignment_id: string }).assignment_id;
    },
    [amEid, refetch],
  );

  return useMemo(
    () => ({
      rows,
      assistsIn,
      isLoading,
      error,
      refetch,
      updateAssignment,
      createAssignment,
      setActive,
      setSchedule,
      createAssist,
    }),
    [
      rows,
      assistsIn,
      isLoading,
      error,
      refetch,
      updateAssignment,
      createAssignment,
      setActive,
      setSchedule,
      createAssist,
    ],
  );
}

// ---------------------------------------------------------------------------
// useFacilityOptions — active facilities for the ad-hoc + template pickers.
// ---------------------------------------------------------------------------

export interface FacilityOption {
  facility_id: string;
  facility_name: string | null;
  account_status_rag: RagStatus | null;
  // Enriched client-side: `active_facilities` has no `service_state` and is
  // under a standing instruction not to be changed.
  service_state?: string | null;
}

export function useFacilityOptions(): {
  options: FacilityOption[];
  loading: boolean;
  error: string | null;
} {
  const [options, setOptions] = useState<FacilityOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data, error: e } = await supabase
          .from('active_facilities')
          .select('facility_id, facility_name, account_status_rag')
          .order('facility_name', { ascending: true });
        if (cancelled) return;
        if (e) throw e;
        const opts = (data ?? []) as unknown as FacilityOption[];
        // The picker is where an ad-hoc stop gets added, so it is exactly
        // where knowing the site is paused changes the decision.
        const stateByFac = await fetchServiceStates(
          opts.map((f) => f.facility_id),
        );
        if (cancelled) return;
        setOptions(withServiceState(opts, stateByFac));
      } catch (e) {
        if (cancelled) return;
        console.error('[am-routes] facility options failed', e);
        setError(errorMessage(e, 'Failed to load facilities.'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return { options, loading, error };
}

// ---------------------------------------------------------------------------
// useTodayBoard — every AM's due stops for today (Exec "today" board).
// Read-only; no day-layer overrides (the exec view is observational and
// shows the planned rotation, per handoff §3B).
// ---------------------------------------------------------------------------

export function useTodayBoard(): {
  rows: TodayViewRow[];
  /** Paused facilities dropped from the board, counted by facility. */
  pausedHidden: number;
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
} {
  const [rows, setRows] = useState<TodayViewRow[]>([]);
  const [pausedHidden, setPausedHidden] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  const refetch = useCallback(() => setTick((n) => n + 1), []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setIsLoading(true);
      setError(null);
      try {
        const { data, error: e } = await supabase
          .from('v_am_routes_today')
          .select(TODAY_COLS)
          .order('am_name', { ascending: true })
          .order('sort_order', { ascending: true });
        if (cancelled) return;
        if (e) throw e;
        const boardRows = (data ?? []) as unknown as TodayViewRow[];
        const stateByFac = await fetchServiceStates(
          boardRows.map((r) => r.facility_id),
        );
        if (cancelled) return;
        // The exec board is a "today" surface, so it follows the same rule as
        // an AM's own Today. An exec asking "where is my team" should not be
        // shown a site nobody is driving to.
        const split = partitionByService(withServiceState(boardRows, stateByFac));
        setRows(split.serviced);
        setPausedHidden(new Set(split.paused.map((r) => r.facility_id)).size);
      } catch (e) {
        if (cancelled) return;
        console.error('[am-routes] today board load failed', e);
        setError(errorMessage(e, 'Failed to load board.'));
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tick]);

  return useMemo(
    () => ({ rows, pausedHidden, isLoading, error, refetch }),
    [rows, pausedHidden, isLoading, error, refetch],
  );
}

// ---------------------------------------------------------------------------
// useFacilityDirectory — every Active/Onboarding facility with its owning
// AM (via v_am_name — employees RLS blocks AMs), account name, and main
// cleaner (service_assignments.worker_name, else provider primary_poc_name).
// Powers the template add modal's two scopes (AFB-0063 3e): "My facilities"
// vs "Someone else's". Flat-fetch + IN-lookup + client-join, house pattern.
// ---------------------------------------------------------------------------

export interface DirectoryFacility {
  facility_id: string;
  facility_name: string | null;
  facility_status: string | null;
  account_id: string | null;
  account_name: string | null;
  account_status_rag: RagStatus | null;
  owner_eid: string | null;
  owner_name: string | null;
  cleaner_name: string | null;
  service_state: string | null;
}

export function useFacilityDirectory(): {
  facilities: DirectoryFacility[];
  loading: boolean;
  error: string | null;
} {
  const [facilities, setFacilities] = useState<DirectoryFacility[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data: facData, error: facErr } = await supabase
          .from('facilities')
          .select(
            'facility_id, facility_name, status, account_id, account_manager_eid, account_status_rag, service_state',
          )
          .in('status', ['Active', 'Onboarding'])
          .order('facility_name', { ascending: true });
        if (cancelled) return;
        if (facErr) throw new Error(errorMessage(facErr, 'Failed to load facilities.'));
        const facs = (facData ?? []) as Array<{
          facility_id: string;
          facility_name: string | null;
          status: string | null;
          account_id: string | null;
          account_manager_eid: string | null;
          account_status_rag: RagStatus | null;
          service_state: string | null;
        }>;

        const accountIds = Array.from(
          new Set(facs.map((f) => f.account_id).filter(Boolean)),
        ) as string[];
        const ownerEids = Array.from(
          new Set(facs.map((f) => f.account_manager_eid).filter(Boolean)),
        ) as string[];
        const facIds = facs.map((f) => f.facility_id);

        const [accRes, amRes, saRes] = await Promise.all([
          accountIds.length
            ? supabase.from('accounts').select('account_id, account_name').in('account_id', accountIds)
            : Promise.resolve({ data: [] as unknown[], error: null }),
          ownerEids.length
            ? supabase.from('v_am_name').select('employee_id, display_name').in('employee_id', ownerEids)
            : Promise.resolve({ data: [] as unknown[], error: null }),
          facIds.length
            ? supabase
                .from('service_assignments')
                .select('facility_id, provider_id, worker_name, assignment_status')
                .in('facility_id', facIds)
                .eq('assignment_status', 'Active')
            : Promise.resolve({ data: [] as unknown[], error: null }),
        ]);
        if (cancelled) return;

        const accName: Record<string, string | null> = {};
        for (const a of (accRes.data ?? []) as Array<{ account_id: string; account_name: string | null }>) {
          accName[a.account_id] = a.account_name;
        }
        const amName: Record<string, string | null> = {};
        for (const a of (amRes.data ?? []) as Array<{ employee_id: string; display_name: string | null }>) {
          amName[a.employee_id] = a.display_name;
        }

        const sas = (saRes.data ?? []) as Array<{
          facility_id: string;
          provider_id: string | null;
          worker_name: string | null;
        }>;
        const provIds = Array.from(
          new Set(sas.map((s) => s.provider_id).filter(Boolean)),
        ) as string[];
        const pocByProvider: Record<string, string | null> = {};
        if (provIds.length > 0) {
          const { data: spData } = await supabase
            .from('service_providers')
            .select('provider_id, primary_poc_name')
            .in('provider_id', provIds);
          if (cancelled) return;
          for (const p of (spData ?? []) as Array<{ provider_id: string; primary_poc_name: string | null }>) {
            pocByProvider[p.provider_id] = p.primary_poc_name;
          }
        }
        const cleanerByFac: Record<string, string | null> = {};
        for (const s of sas) {
          if (cleanerByFac[s.facility_id]) continue;
          const poc = s.provider_id ? pocByProvider[s.provider_id] : null;
          const name = s.worker_name?.trim() || poc?.trim() || null;
          if (name) cleanerByFac[s.facility_id] = name;
        }

        setFacilities(
          facs.map((f) => ({
            facility_id: f.facility_id,
            facility_name: f.facility_name,
            facility_status: f.status,
            account_id: f.account_id,
            account_name: f.account_id ? accName[f.account_id] ?? null : null,
            account_status_rag: f.account_status_rag,
            owner_eid: f.account_manager_eid,
            owner_name: f.account_manager_eid ? amName[f.account_manager_eid] ?? null : null,
            cleaner_name: cleanerByFac[f.facility_id] ?? null,
            service_state: f.service_state ?? null,
          })),
        );
      } catch (e) {
        if (cancelled) return;
        console.error('[am-routes] facility directory failed', e);
        setError(errorMessage(e, 'Failed to load facilities.'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return { facilities, loading, error };
}
